import {
  ActionIcon,
  Button,
  Flex,
  Stack,
  Textarea,
  TextInput,
} from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { type UseFieldArrayReturn, useFormContext } from "react-hook-form";
import ConfirmationButton from "src/components/shared/ConfirmationButton/ConfirmationButton";
import { useCheckPerm, useIsOnline } from "src/hooks";
import {
  type ILiftingEquipmentForm,
  type ILiftingEquipmentItem,
} from "src/pages/Settings/YachtSetup/LiftingEquipment/LiftingEquipment.types";

type Props = Omit<UseFieldArrayReturn<ILiftingEquipmentForm>, "append"> & {
  data: ILiftingEquipmentItem[];
};

const LiftingEquipmentForm = ({ fields, remove, data }: Props) => {
  const { register, getValues, reset } =
    useFormContext<ILiftingEquipmentForm>();
  const isOnline = useIsOnline();
  const checkPerm = useCheckPerm();
  const readOnly = !checkPerm("editparticulars") || !isOnline;

  const handleAddCert = (rowIndex: number) => {
    const currentValues = getValues();
    const updated = currentValues.list.map((item, i) =>
      i === rowIndex
        ? { ...item, certificates: [...(item.certificates ?? []), ""] }
        : item,
    );
    reset({ list: updated });
  };

  const handleRemoveCert = (rowIndex: number, certIndex: number) => {
    const currentValues = getValues();
    const existing = currentValues.list[rowIndex]?.certificates ?? [];
    const updated = existing.filter((_, i) => i !== certIndex);
    const updatedList = currentValues.list.map((item, i) =>
      i === rowIndex ? { ...item, certificates: updated } : item,
    );
    reset({ list: updatedList });
  };

  const handleRemoveRow = (index: number) => {
    remove(index);
  };

  return (
    <Flex direction="row" gap={24} mt={30} px={16}>
      {fields.map((field, rowIndex) => {
        const certCount = field.certificates?.length;
        return (
          <Flex key={field.id} direction="column" gap={8}>
            {!readOnly && (
              <ConfirmationButton
                modalText={`Are you sure you want to remove this piece of equipment?`}
                onConfirm={() => handleRemoveRow(rowIndex)}
                buttonProps={{
                  variant: "transparent",
                  style: {
                    backgroundColor: "transparent",
                    padding: 0,
                  },
                  styles: {
                    label: {
                      width: "100%",
                    },
                  },
                }}>
                <ActionIcon color="red" variant="outline" w="100%">
                  <IconTrash size={20} />
                </ActionIcon>
              </ConfirmationButton>
            )}
            <Stack spacing={8} style={{ flex: 1, minWidth: 200 }}>
              <TextInput
                label="Name"
                readOnly={readOnly}
                {...register(`list.${rowIndex}.name`)}
              />
              <Textarea
                autosize
                maxRows={4}
                minRows={4}
                label="Description"
                readOnly={readOnly}
                {...register(`list.${rowIndex}.description`)}
              />
              <TextInput
                label="Distinct Numbers or Marks"
                readOnly={readOnly}
                {...register(`list.${rowIndex}.distinct`)}
              />
            </Stack>

            <Stack
              spacing={8}
              style={{ flex: 1, minWidth: 200 }}
              align="flex-start">
              {Array.from({ length: certCount }, (_, certIndex) => (
                <Flex
                  key={certIndex}
                  direction="row"
                  gap={4}
                  align="center"
                  w="100%">
                  <TextInput
                    label={certIndex === 0 ? "Certificates" : undefined}
                    style={{ flex: 1 }}
                    readOnly={readOnly}
                    {...register(`list.${rowIndex}.certificates.${certIndex}`)}
                  />
                  {!readOnly && (
                    <ConfirmationButton
                      modalText="Are you sure you want to remove this certificate?"
                      onConfirm={() => handleRemoveCert(rowIndex, certIndex)}
                      buttonProps={{
                        variant: "transparent",
                        disabled: certCount <= 1,
                        style: {
                          backgroundColor: "transparent",
                          marginTop: certIndex === 0 ? 25 : 0,
                          paddingRight: 0,
                          paddingLeft: 4,
                        },
                      }}>
                      <ActionIcon
                        color="red"
                        variant="subtle"
                        disabled={certCount <= 1}>
                        <IconTrash size={18} />
                      </ActionIcon>
                    </ConfirmationButton>
                  )}
                </Flex>
              ))}
              {!readOnly && (
                <Button
                  size="xs"
                  variant="outline"
                  w="100%"
                  mt={2}
                  leftIcon={<IconPlus size="1rem" />}
                  onClick={() => handleAddCert(rowIndex)}>
                  Add Certificate
                </Button>
              )}
            </Stack>
          </Flex>
        );
      })}
    </Flex>
  );
};

export default LiftingEquipmentForm;
